/**
 * Deployment detail: Terraform plan review & approval panel.
 *
 * Generic over the product: a Terraform plan that an administrator other than the requester must approve, with
 * the plan history shape and the POST approve-plan / reject-plan endpoints. The product's wrapper
 * supplies the detail query (its own GET endpoint) and the copy.
 * The decision confirm is built into the panel (no window.confirm).
 */
import { Fragment, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { CheckCircle2, ExternalLink, ShieldCheck, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/common/Card'
import { Button } from '@/components/common/Button'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { useApprovePlan, useRejectPlan } from '@/hooks/useDeployments'
import { formatDateTime } from '@/utils/format'
import type { DeploymentStatus, TerraformPlanRecord } from '@/types'
import {
  PLAN_STATUS_LABELS, PLAN_STATUS_TONE, canDecidePlan, formatPlanCounts, latestPlan,
} from './planStatus'
import {
  applyIsDisabled, planDecisionErrorMessage, selfApprovalHint, waitingForApprovalText, type PlanApprovalCopy, type PlanDetailLike,
  type PlanRecord,
} from './planApproval'
import styles from './PlanApproval.module.css'

const RESOURCE_PREVIEW = 8

export function PlanPill({ plan }: { plan: Pick<TerraformPlanRecord, 'status'> }) {
  return <span className={clsx(styles.pill, styles[PLAN_STATUS_TONE[plan.status] ?? 'muted'])}>{PLAN_STATUS_LABELS[plan.status] ?? plan.status}</span>
}

export function RunLinks({ plan }: { plan: Pick<TerraformPlanRecord, 'plan_run_url' | 'apply_run_url'> }) {
  return (
    <span className={styles.links}>
      {plan.plan_run_url && <a href={plan.plan_run_url} target="_blank" rel="noopener noreferrer">Plan run <ExternalLink size={11} /></a>}
      {plan.apply_run_url && <a href={plan.apply_run_url} target="_blank" rel="noopener noreferrer">Apply run <ExternalLink size={11} /></a>}
    </span>
  )
}

export function PlanSummary({ plan }: { plan: PlanRecord }) {
  const [showAll, setShowAll] = useState(false)
  const [showText, setShowText] = useState(false)
  const s = plan.plan_summary
  const resources = s?.resources ?? []
  const visible = showAll ? resources : resources.slice(0, RESOURCE_PREVIEW)
  return (
    <>
      {s && (
        <div className={styles.counts} aria-label={formatPlanCounts(plan)}>
          <span className={clsx(styles.count, styles.add)}><strong>+{s.add}</strong> to add</span>
          <span className={clsx(styles.count, styles.change)}><strong>~{s.change}</strong> to change</span>
          <span className={clsx(styles.count, styles.destroy)}><strong>-{s.destroy}</strong> to destroy</span>
        </div>
      )}
      {resources.length > 0 && (
        <>
          <div className={styles.sectionLabel}>Resources ({resources.length})</div>
          <ul className={styles.resourceList}>
            {visible.map((r) => <li key={r}>{r}</li>)}
          </ul>
          {resources.length > RESOURCE_PREVIEW && (
            <button type="button" className={styles.linkBtn} onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `Show all ${resources.length} resources`}
            </button>
          )}
        </>
      )}
      {plan.plan_text && (
        <>
          <div className={styles.sectionLabel}>Plan output</div>
          <button type="button" className={styles.linkBtn} onClick={() => setShowText((v) => !v)} aria-expanded={showText}>
            {showText ? 'Hide terraform plan' : 'Show terraform plan'}
          </button>
          {showText && <pre className={styles.planText}>{plan.plan_text}</pre>}
        </>
      )}
    </>
  )
}

type Decision = 'approve' | 'reject'

interface DecisionProps {
  deploymentId: number
  plan: PlanRecord
  /** Completes the sentence "…for exactly this plan (counts)" — e.g. ", then verifies the server." */
  applyNote?: ReactNode
  confirmApproveText: string | ((plan: PlanRecord) => string)
  /** The local executor has apply switched off: approving would create nothing, so it is not offered. */
  applyDisabled?: boolean
  /** Product-specific wording (default: the terraform wording). */
  copy?: PlanApprovalCopy
  /** "You can approve your own plan in this local environment." — shown when self approval applies. */
  selfApproval?: string | null
}

export function DecisionPanel({ deploymentId, plan, applyNote, confirmApproveText, applyDisabled = false, copy, selfApproval }: DecisionProps) {
  const [notes, setNotes] = useState('')
  const [confirming, setConfirming] = useState<Decision | null>(null)
  const approve = useApprovePlan()
  const reject = useRejectPlan()
  const pending = approve.isPending || reject.isPending
  const error = approve.error ?? reject.error

  const submit = (d: Decision) => {
    const m = d === 'approve' ? approve : reject
    m.mutate({ id: deploymentId, notes: notes.trim() }, { onSettled: () => setConfirming(null) })
  }

  return (
    <div className={styles.decision}>
      <span className={styles.decisionTitle}><ShieldCheck size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />Plan approval required</span>
      <span style={{ fontSize: 13, color: 'var(--aptean-text-secondary)' }}>
        {applyDisabled ? (
          <>
            Review the plan above ({formatPlanCounts(plan)}). <strong>Apply is disabled in this environment</strong>, so
            approving is not available and nothing can be created. Rejecting cancels the deployment.
          </>
        ) : (
          <>
            Review the plan above.{' '}
            {copy?.describeApprove
              ? copy.describeApprove(formatPlanCounts(plan))
              : <>Approving runs <strong>terraform apply</strong> for exactly this plan ({formatPlanCounts(plan)}){applyNote}</>}
            {' '}Rejecting cancels the deployment — nothing is created.
          </>
        )}
      </span>
      {selfApproval && <span role="note" style={{ fontSize: 13, fontWeight: 600 }}>{selfApproval}</span>}
      <textarea className={styles.textarea} rows={2} placeholder="Notes (optional)" value={notes}
        disabled={pending} onChange={(e) => setNotes(e.target.value)} />
      {confirming ? (
        <>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {confirming === 'approve'
              ? (typeof confirmApproveText === 'function' ? confirmApproveText(plan) : confirmApproveText)
              : 'Confirm: reject this plan and cancel the deployment?'}
          </span>
          <div className={styles.actions}>
            <Button size="sm" variant={confirming === 'approve' ? 'primary' : 'danger'} loading={pending}
              icon={confirming === 'approve' ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
              onClick={() => submit(confirming)}>
              {confirming === 'approve' ? (copy?.confirmApproveButton ?? 'Yes, apply plan') : 'Yes, reject plan'}
            </Button>
            <Button size="sm" variant="secondary" disabled={pending} onClick={() => setConfirming(null)}>Back</Button>
          </div>
        </>
      ) : (
        <div className={styles.actions}>
          <Button size="sm" icon={<CheckCircle2 size={13} />} disabled={applyDisabled}
            title={applyDisabled ? 'Apply is disabled in this environment' : undefined}
            onClick={() => setConfirming('approve')}>{copy?.approveButton ?? 'Approve plan'}</Button>
          <Button size="sm" variant="danger" icon={<XCircle size={13} />} onClick={() => setConfirming('reject')}>Reject plan</Button>
        </div>
      )}
      {error && <span className={styles.errorText}>{planDecisionErrorMessage(error)}</span>}
    </div>
  )
}

export interface PlanApprovalPanelProps<TConfig> {
  deploymentId: number
  status: DeploymentStatus | string
  /** The product's detail query (Apprise: apprise-vm/). */
  query: { data?: PlanDetailLike<TConfig>; isLoading: boolean; isError: boolean }
  title: string
  loadErrorText: string
  /** See DecisionPanel.applyNote. */
  applyNote?: ReactNode
  confirmApproveText: string | ((plan: PlanRecord) => string)
  /** Product-specific wording of the decision panel. */
  copy?: PlanApprovalCopy
  /** Resolved-configuration block shown under "Show resolved configuration". */
  renderConfig?: (config: TConfig) => ReactNode
  /** Plan outputs as a key/value list. Apprise shows its own server block instead. */
  showOutputs?: boolean
}

export function PlanApprovalPanel<TConfig>({
  deploymentId, status, query, title, loadErrorText, applyNote, confirmApproveText, copy, renderConfig, showOutputs = true,
}: PlanApprovalPanelProps<TConfig>) {
  const { data, isLoading, isError } = query
  const [showConfig, setShowConfig] = useState(false)
  const plan = latestPlan(data?.plans)
  const history = data?.plans ?? []
  const outputs = plan?.outputs ? Object.entries(plan.outputs).filter(([, v]) => v !== '' && v != null) : []
  const executor = data?.executor
  const isLocal = executor?.mode === 'local'
  const applyDisabled = applyIsDisabled(executor)

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={plan ? `Created ${formatDateTime(plan.created_at)}` : 'Infrastructure is planned first, then applied after approval'}
        actions={plan ? <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}><RunLinks plan={plan} /><PlanPill plan={plan} /></span> : undefined}
      />
      <CardContent>
        {isLocal && executor?.note && (
          <div role="note" className={styles.sectionLabel}
            style={{ textTransform: 'none', fontWeight: 600, letterSpacing: 0, marginBottom: 12 }}>
            {executor.note}
          </div>
        )}
        {isLoading ? (
          <LoadingSpinner label="Loading plan…" />
        ) : isError ? (
          <span className={styles.errorText}>{loadErrorText}</span>
        ) : !plan ? (
          status === 'planning_infra'
            ? <LoadingSpinner label={isLocal ? 'Terraform plan is running on this machine…' : 'Terraform plan is running in GitHub Actions…'} />
            : <span style={{ fontSize: 13, color: 'var(--aptean-text-muted)' }}>No plan yet — it is produced once the deployment is triggered.</span>
        ) : (
          <>
            <PlanSummary key={plan.id} plan={plan} />
            {canDecidePlan(data, status) && (
              <DecisionPanel deploymentId={deploymentId} plan={plan} applyNote={applyNote}
                confirmApproveText={confirmApproveText} applyDisabled={applyDisabled} copy={copy}
                selfApproval={selfApprovalHint(executor, !!data?.can_approve)} />
            )}
            {status === 'awaiting_plan_approval' && waitingForApprovalText(executor, !!data?.can_approve) && (
              <div className={styles.sectionLabel} style={{ textTransform: 'none', fontWeight: 500, letterSpacing: 0 }}>
                {waitingForApprovalText(executor, !!data?.can_approve)}
              </div>
            )}
            {plan.approved_by && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--aptean-text-muted)' }}>
                Decision by {String(plan.approved_by)}{plan.approved_at ? ` · ${formatDateTime(plan.approved_at)}` : ''}
              </div>
            )}
            {showOutputs && outputs.length > 0 && (
              <>
                <div className={styles.sectionLabel}>Outputs</div>
                <dl className={styles.kv}>
                  {outputs.map(([k, v]) => (<Fragment key={k}><dt>{k}</dt><dd>{String(v)}</dd></Fragment>))}
                </dl>
              </>
            )}
          </>
        )}

        {history.length > 1 && (
          <>
            <div className={styles.sectionLabel}>Plan history</div>
            <div className={styles.history}>
              {history.map((p) => (
                <div key={p.id} className={styles.historyRow}>
                  <span>{formatDateTime(p.created_at)} · {formatPlanCounts(p)}</span>
                  <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}><RunLinks plan={p} /><PlanPill plan={p} /></span>
                </div>
              ))}
            </div>
          </>
        )}

        {data?.config && renderConfig && (
          <>
            <button type="button" className={styles.linkBtn} style={{ marginTop: 16 }} onClick={() => setShowConfig((v) => !v)}
              aria-expanded={showConfig}>
              {showConfig ? 'Hide resolved configuration' : 'Show resolved configuration'}
            </button>
            {showConfig && <div style={{ marginTop: 12 }}>{renderConfig(data.config)}</div>}
          </>
        )}
      </CardContent>
    </Card>
  )
}
